import { useMutation } from "@apollo/client";
import { Button, dataModuleString, Typography } from "@lyfegen-platform/react-common";
import { Box, Divider, makeStyles, Popover, Switch } from "@material-ui/core";
import { DocumentNode } from "graphql";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import {
	UPDATE_AGREEMENTS_TABLE_COLUMNS,
	UPDATE_DOCUMENTS_TABLE_COLUMNS,
	UPDATE_FORMS_TABLE_COLUMNS,
	UPDATE_RECORDS_TABLE_COLUMNS,
	UPDATE_SIMULATIONS_TABLE_COLUMNS,
	UPDATE_TRANSACTIONS_IN_CREDIT_NOTE_DOCUMENT_TABLE_COLUMNS,
	UPDATE_TRANSACTIONS_IN_FREE_OF_CHARGE_DOCUMENT_TABLE_COLUMNS,
	UPDATE_TRANSACTIONS_IN_REFUND_INVOICE_DOCUMENT_TABLE_COLUMNS,
	UPDATE_TRANSACTIONS_TABLE_COLUMNS,
} from "../../containers/user/queries";
import { buttonHover } from "../../hooks/use-map-theme-styles";
import { modelsModuleString } from "../../hooks/useModelsModule";
import { EMPTY_VALUE_PLACEHOLDER } from "../../models/common/data";
import { Column } from "../../types/tableTypes";
import { userPersonalInfoState } from "../../store/user";
import ColumnsIcon from "../icons/ColumnsIcon";
import { TableTypes } from "./LyfeDataTable";
import { getColumnsByState } from "./utils/getFilterPopupState";

export enum ConfigurableTableType {
	AGREEMENTS,
	TRANSACTIONS,
	DOCUMENTS,
	TRANSACTIONS_IN_REFUND_INVOICE_DOCUMENT,
	TRANSACTIONS_IN_CREDIT_NOTE_DOCUMENT,
	TRANSACTIONS_IN_FREE_OF_CHARGE_DOCUMENT,
	RECORDS,
	FORMS,
}

export interface IColumnsCardProps {
	title: TableTypes;
	columns: Array<Column<any>>;
	configurableTableType: ConfigurableTableType;
}

type TableConfig = {
	updateQuery: DocumentNode;
	module?: string;
	updateQueryVariableKey: string;
	disabledColumns: Array<string>;
	userStateKey:
		| "transactionsTableColumns"
		| "documentsTableColumns"
		| "agreementsTableColumns"
		| "simulationsTableColumns"
		| "transactionsInRefundInvoiceDocumentTableColumns"
		| "transactionsInCreditNoteDocumentTableColumns"
		| "transactionsInFreeOfChargeDocumentTableColumns"
		| "recordsTableColumns"
		| "formsTableColumns";
};

const getTableConfig = (configurableTableType: ConfigurableTableType): TableConfig => {
	if (configurableTableType === ConfigurableTableType.TRANSACTIONS) {
		return {
			updateQuery: UPDATE_TRANSACTIONS_TABLE_COLUMNS,
			disabledColumns: ["id"],
			updateQueryVariableKey: "transactionsColumns",
			userStateKey: "transactionsTableColumns",
		};
	}

	if (configurableTableType === ConfigurableTableType.DOCUMENTS) {
		return {
			updateQuery: UPDATE_DOCUMENTS_TABLE_COLUMNS,
			disabledColumns: ["id"],
			updateQueryVariableKey: "documentsColumns",
			userStateKey: "documentsTableColumns",
		};
	}

	if (configurableTableType === ConfigurableTableType.TRANSACTIONS_IN_REFUND_INVOICE_DOCUMENT) {
		return {
			updateQuery: UPDATE_TRANSACTIONS_IN_REFUND_INVOICE_DOCUMENT_TABLE_COLUMNS,
			disabledColumns: ["id", "date", "refundAmount"],
			updateQueryVariableKey: "transactionsInRefundInvoiceDocumentTableColumns",
			userStateKey: "transactionsInRefundInvoiceDocumentTableColumns",
		};
	}

	if (configurableTableType === ConfigurableTableType.TRANSACTIONS_IN_CREDIT_NOTE_DOCUMENT) {
		return {
			updateQuery: UPDATE_TRANSACTIONS_IN_CREDIT_NOTE_DOCUMENT_TABLE_COLUMNS,
			disabledColumns: ["id", "date", "refundAmount"],
			updateQueryVariableKey: "transactionsInCreditNoteDocumentTableColumns",
			userStateKey: "transactionsInCreditNoteDocumentTableColumns",
		};
	}

	if (configurableTableType === ConfigurableTableType.TRANSACTIONS_IN_FREE_OF_CHARGE_DOCUMENT) {
		return {
			updateQuery: UPDATE_TRANSACTIONS_IN_FREE_OF_CHARGE_DOCUMENT_TABLE_COLUMNS,
			disabledColumns: ["id", "date", "refundAmount"],
			updateQueryVariableKey: "transactionsInFreeOfChargeDocumentTableColumns",
			userStateKey: "transactionsInFreeOfChargeDocumentTableColumns",
		};
	}

	if (configurableTableType === ConfigurableTableType.AGREEMENTS) {
		return {
			updateQuery: UPDATE_AGREEMENTS_TABLE_COLUMNS,
			disabledColumns: ["name"],
			updateQueryVariableKey: "agreementsColumns",
			userStateKey: "agreementsTableColumns",
		};
	}

	if (configurableTableType === ConfigurableTableType.RECORDS) {
		return {
			updateQuery: UPDATE_RECORDS_TABLE_COLUMNS,
			disabledColumns: ["id"],
			updateQueryVariableKey: "recordsColumns",
			userStateKey: "recordsTableColumns",
		};
	}

	if (configurableTableType === ConfigurableTableType.FORMS) {
		return {
			updateQuery: UPDATE_FORMS_TABLE_COLUMNS,
			disabledColumns: ["id"],
			updateQueryVariableKey: "formsColumns",
			userStateKey: "formsTableColumns",
		};
	}

	return {
		updateQuery: UPDATE_SIMULATIONS_TABLE_COLUMNS,
		module: modelsModuleString,
		disabledColumns: ["name"],
		updateQueryVariableKey: "simulationsColumns",
		userStateKey: "simulationsTableColumns",
	};
};

const LyfeColumnsCard = (props: IColumnsCardProps) => {
	const tableConfig = getTableConfig(props.configurableTableType);
	const columnsRef = useRef(null);
	const { t } = useTranslation();
	const [anchorElColumns, setAnchorElColumns] = useRecoilState<HTMLElement | null>(getColumnsByState());
	const [user, setUser] = useRecoilState(userPersonalInfoState);
	const [update] = useMutation(tableConfig.updateQuery, { context: { module: tableConfig.module ?? dataModuleString } });
	const classes = useStyles({});
	const handleClickColumns = () => {
		setAnchorElColumns(columnsRef.current);
	};

	const handleColumnsPopoverClose = () => {
		setAnchorElColumns(null);
	};

	const columnsPopoverOpen = Boolean(anchorElColumns);
	const handleUpdateColumns = (field: string | undefined) => {
		if (!field || tableConfig.disabledColumns.includes(field)) return;

		const columns = [
			...(user[tableConfig.userStateKey].includes(field)
				? user[tableConfig.userStateKey].filter((column) => column !== field)
				: [...user[tableConfig.userStateKey], field]),
		];
		setUser({
			...user,
			[tableConfig.userStateKey]: columns,
		});
		update({ variables: { [tableConfig.updateQueryVariableKey]: columns } });
	};
	const handleResetDefault = () => {
		const columns = props.columns.filter((column) => column.default).map((column) => column.field as string);
		setUser({
			...user,
			[tableConfig.userStateKey]: columns,
		});
		update({ variables: { [tableConfig.updateQueryVariableKey]: columns } });
	};

	useEffect(() => {
		handleColumnsPopoverClose();
	}, [props.configurableTableType]);

	return (
		<>
			<span ref={columnsRef}>
				<Button
					disableRipple
					disableElevation
					onClick={handleClickColumns}
					classes={{ text: classes.buttonText, root: classes.buttonRootColumns }}
					id="tableColumnsButton"
				>
					<ColumnsIcon />
					&nbsp; &nbsp;
					<Typography color="primary" type="tableHeading" onClick={handleClickColumns}>
						{t("Columns")}
					</Typography>
				</Button>
			</span>
			<Popover
				id="actions-popover"
				open={columnsPopoverOpen}
				anchorEl={columnsRef.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				PaperProps={{
					className: classes.root,
				}}
				onClose={handleColumnsPopoverClose}
				style={{ zIndex: 3 }}
			>
				{
					<Box maxHeight="512px">
						<div>
							<Box className={classes.switchBox}>
								<Box display="flex">
									<table>
										<tbody>
											{props.columns.map((n, i) => (
												<tr key={i} style={{ padding: "12px 16px" }}>
													<td>
														<Switch
															color={
																user[tableConfig.userStateKey].includes(n?.field as string) ? "primary" : undefined
															}
															onChange={() => handleUpdateColumns(n?.field as string)}
															checked={user[tableConfig.userStateKey].includes(n?.field as string)}
															value={n?.field as string}
															disabled={tableConfig.disabledColumns.includes(n?.field as string)}
														/>
													</td>
													<td>{t((n?.title as string) || EMPTY_VALUE_PLACEHOLDER)}</td>
												</tr>
											))}
										</tbody>
									</table>
								</Box>
							</Box>
							<Box style={{ paddingTop: "4px" }}>
								<Divider />
								<Button
									disableRipple
									disableElevation
									onClick={handleResetDefault}
									classes={{ text: classes.buttonText, root: classes.buttonRoot, fullWidth: "100%" }}
									id="resetDefaultView"
								>
									{t("reset-to-default-view")}
								</Button>
							</Box>
						</div>
					</Box>
				}
			</Popover>
		</>
	);
};

export default LyfeColumnsCard;

const useStyles = makeStyles(() => ({
	root: {
		borderRadius: "8px",
		boxShadow: "0px 4px 10px rgba(41, 41, 41, 0.08)",
		width: "200px",
		border: "none",
	},
	switchBox: {
		paddingTop: "0px",
		paddingLeft: "0px",
		paddingRight: "10px",
	},
	buttonText: {
		color: "#367bf5",
		fontWeight: 600,
		textTransform: "none",
		textAlign: "center",
		verticalAlign: "middle",
	},
	buttonRoot: {
		margin: "8px 4px 8px 8px",
		"&:hover": {
			backgroundColor: "transparent",
		},
	},
	buttonRootColumns: {
		"&:hover": {
			backgroundColor: buttonHover,
		},
	},
}));
